import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Building2, TrendingUp, Shield, Users } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const T = {
  heading: { de: 'Über Basic United Holding', en: 'About Basic United Holding' },
  p1: {
    de: 'Die Basic United Holding GmbH mit Sitz in Langenfeld ist eine strategisch ausgerichtete Holdinggesellschaft, die als Dach für mehrere spezialisierte Tochterunternehmen dient.',
    en: 'Basic United Holding GmbH, based in Langenfeld, is a strategically oriented holding company that serves as an umbrella for several specialised subsidiaries.',
  },
  p2: {
    de: 'Unser Portfolio umfasst Logistiklösungen, Unternehmensberatung und Dienstleistungen – vereint durch gemeinsame Werte und das Streben nach Exzellenz.',
    en: 'Our portfolio encompasses logistics solutions, business consulting and services – united by shared values and the pursuit of excellence.',
  },
  features: [
    { icon: Building2, title: { de: 'Holding-Struktur', en: 'Holding Structure' }, description: { de: 'Strategische Führung und operative Exzellenz unter einem Dach.', en: 'Strategic leadership and operational excellence under one roof.' } },
    { icon: TrendingUp, title: { de: 'Wachstum', en: 'Growth' }, description: { de: 'Nachhaltige Expansion durch diversifizierte Geschäftsfelder.', en: 'Sustainable expansion through diversified business areas.' } },
    { icon: Shield, title: { de: 'Verlässlichkeit', en: 'Reliability' }, description: { de: 'Langfristige Partnerschaften basierend auf Vertrauen und Integrität.', en: 'Long-term partnerships based on trust and integrity.' } },
    { icon: Users, title: { de: 'Expertise', en: 'Expertise' }, description: { de: 'Erfahrene Führungskräfte mit umfassendem Branchenwissen.', en: 'Experienced leaders with comprehensive industry knowledge.' } },
  ],
  subsidiaries: { de: 'Tochterunternehmen', en: 'Subsidiaries' },
  clients: { de: 'Zufriedene Kunden', en: 'Satisfied Clients' },
  founded: { de: 'Gegründet', en: 'Founded' },
};

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { language } = useLanguage();

  return (
    <section ref={ref} data-testid="about-section" className="section-spacing bg-[#0A1220]">
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
            <div className="accent-line mb-6"></div>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">{T.heading[language]}</h2>
            <p className="text-lg text-white/70 leading-relaxed mb-6">{T.p1[language]}</p>
            <p className="text-lg text-white/70 leading-relaxed mb-8">{T.p2[language]}</p>
            <div className="grid grid-cols-2 gap-6">
              {T.features.map((feature, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#F5A623]/10 flex items-center justify-center rounded-lg">
                    <feature.icon size={20} className="text-[#F5A623]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{feature.title[language]}</h4>
                    <p className="text-sm text-white/50">{feature.description[language]}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
            <div className="relative bg-[#152238] p-8 md:p-12 rounded-2xl border border-[#1e3a5f]">
              <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[#F5A623] rounded-tl-2xl"></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[#F5A623] rounded-br-2xl"></div>
              <div className="space-y-8">
                <div className="text-center">
                  <span className="block font-heading text-6xl md:text-7xl font-bold text-white">3</span>
                  <span className="text-white/50 uppercase tracking-wider text-sm">{T.subsidiaries[language]}</span>
                </div>
                <div className="w-full h-px bg-[#1e3a5f]"></div>
                <div className="grid grid-cols-2 gap-8 text-center">
                  <div>
                    <span className="block font-heading text-3xl md:text-4xl font-bold text-[#F5A623]">100+</span>
                    <span className="text-white/50 text-sm">{T.clients[language]}</span>
                  </div>
                  <div>
                    <span className="block font-heading text-3xl md:text-4xl font-bold text-white">2025</span>
                    <span className="text-white/50 text-sm">{T.founded[language]}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
